import { useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "react-intl"

/**
 * Check if two cosmic school objects have the same locale, selectivity filters,
 * graduate/technical/undergrad types, and are not the same school
 * @param {Object} schoolA - Cosmic school object  
 * @param {Object} schoolB - Cosmic school object  
 * @returns {boolean}
 */
const schoolSelectivityAndTypesMatch = (schoolA, schoolB) => {
  return schoolA.locale === schoolB.locale
    && schoolA.slug !== schoolB.slug
    && schoolA.metadata.school_graduate_type === schoolB.metadata.school_graduate_type
    && schoolA.metadata.school_technical_type === schoolB.metadata.school_technical_type
    && schoolA.metadata.school_undergrad_type === schoolB.metadata.school_undergrad_type
    && schoolA.metadata.school_selectivity_filter?.length
    && schoolB.metadata.school_selectivity_filter?.length
    && schoolA.metadata.school_selectivity_filter.join(",") === schoolB.metadata.school_selectivity_filter.join(",")
}

/**
 * Fetch all schools with the set of selectivity filters and institution types
 * as a given school
 * @param {string} schoolSlug - cosmic slug
 * @returns {Object[]} schools with the same values for the provided fields
 */
export const useSimilarSelectivitySchools = (schoolSlug) => {
  const { locale } = useIntl()
  const { allSchools } = useStaticQuery(graphql`
    query {
      allSchools: allCosmicjsSchools {
        nodes {
          locale
          slug
          title
          metadata {
            school_selectivity_filter
            school_graduate_type
            school_technical_type
            school_undergrad_type
          }
        }
      }
    }
  `)

  const matchingSchools = useMemo(() => {
    const schoolNode = allSchools.nodes.find(node => 
      node.slug === schoolSlug && node.locale === locale
    )
    if (!schoolNode) {
      return []
    }
    return allSchools.nodes.filter(node => schoolSelectivityAndTypesMatch(schoolNode, node))
  }, [allSchools.nodes, schoolSlug, locale])
  
  return matchingSchools
}
